import type { ReactNode } from 'react';
import { Menu } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { toggleCollapse, toggleMobileOpen } from '@kss/layouts';
import SearchBar from './SearchBar';
import ThemeSwitcher from './ThemeSwitcher';
import ColorModeToggle from './ColorModeToggle';
import FullscreenToggle from './FullscreenToggle';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';
import UserProfileDropdown from './UserProfileDropdown';
import Breadcrumb from './Breadcrumb';

export interface TopBarProps {
  /** Optional slot rendered just before the language switcher. Useful
   *  for app-specific status indicators (offline-sync badge, branch
   *  switcher, etc.) without leaking app deps into @kss/ui. */
  extras?: ReactNode;
}

export default function TopBar({ extras }: TopBarProps = {}) {
  const dispatch = useDispatch();

  return (
    <header className="no-print flex h-14 items-center border-b border-border bg-surface-topbar px-4">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            // On mobile (md breakpoint and below) this toggles the
            // drawer overlay; on desktop it collapses the inline
            // sidebar. Dispatching both is harmless because each
            // state only affects layout at its own breakpoint.
            dispatch(toggleMobileOpen());
            // Desktops: only toggle collapse when viewport is wide
            // enough that the drawer overlay isn't in play. Avoids
            // double-clicking on phones leaving the sidebar collapsed
            // for next desktop session.
            if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches) {
              dispatch(toggleCollapse());
            }
          }}
          aria-label="Toggle navigation menu"
          className="flex h-10 w-10 items-center justify-center rounded-lg text-content-muted hover:bg-surface-card-hover hover:text-content-primary transition-colors md:h-8 md:w-8"
        >
          <Menu size={20} className="md:size-[18px]" />
        </button>
        <Breadcrumb />
      </div>

      {/* Center - Search */}
      <div className="mx-4 flex-1 max-w-md">
        <SearchBar />
      </div>

      {/* Right */}
      <div className="flex items-center gap-1">
        {extras}
        <ThemeSwitcher />
        <ColorModeToggle />
        <FullscreenToggle />
        <LanguageSwitcher />
        <NotificationBell />
        <UserProfileDropdown />
      </div>
    </header>
  );
}
